import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import GenericButton, { GenericButtonTypeEnum } from './GenericButton';
import { ReactComponent as ImportIcon } from '../assets/import-icon.svg';
import { ImportDto } from '../common/interfaces/kindergarten.interface';
import { createNotification } from '../utilities/helper/notification.helper';
import { CustomErrorStatus, MESSAGE_TYPE } from '../common/constants/common.constants';
import { importChildren, importUsers } from '../api/kindergarten.api';
import { CreateImportUserTypeEnum, ImportResultStatus, ImportUserType } from '../common/enums/common.enum';
import GenericInfoModal from './GenericInfoModal';
import { Box, Typography } from '@mui/material';

interface GenericInputButtonProps {
  label: string;
  disabled?: boolean;
  type: ImportUserType;
  kindergartenId: string;
  onSuccessImport: () => void;
}

const accept = '.xls, .xlsx';

const GenericImportButton: FC<GenericInputButtonProps> = ({ label, disabled, type, kindergartenId, onSuccessImport }) => {
  const [importing, setImporting] = useState(false);
  const [importWarning, setImportWarning] = useState<ImportDto[]>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setImporting(true);
      const file = e.target.files?.[0];
      if (file) {
        const res: ImportDto[] = [];
        if (type === ImportUserType.CHILDREN) {
          const results = (await importChildren(file, kindergartenId)).results;
          if (results) {
            res.push(...results);
          }
        } else {
          const results = (
            await importUsers(
              file,
              kindergartenId,
              type === ImportUserType.PARENT ? CreateImportUserTypeEnum.PARENT : CreateImportUserTypeEnum.TEACHER
            )
          ).results;
          if (results) {
            res.push(...results);
          }
        }
        if (res.find(item => item.status === ImportResultStatus.WARNING)) {
          setImportWarning(res.filter(item => item.status === ImportResultStatus.WARNING));
        }
        onSuccessImport();
        createNotification(MESSAGE_TYPE.SUCCESS, 'Import finished successfully');
      }
    } catch (error: any) {
      const backendStatus = error?.response?.data?.status;
      if (backendStatus === CustomErrorStatus.MISSING_EXCEL_VALUE) {
        createNotification(MESSAGE_TYPE.ERROR, 'Excel has missing values');
      } else {
        createNotification(MESSAGE_TYPE.ERROR, 'Failed to import');
      }
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <>
      <GenericButton
        label={label}
        buttonType={GenericButtonTypeEnum.Secondary}
        variant="contained"
        startIcon={<ImportIcon />}
        onClick={handleClick}
        isLoading={importing}
        disabled={disabled}
      />
      <input type="file" accept={accept} ref={fileInputRef} style={{ display: 'none' }} onChange={handleChange} />
      {importWarning && (
        <GenericInfoModal open={true} onClose={() => setImportWarning(undefined)} label="Import warning" closeLabel="Close">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {importWarning.map((item, index) => (
              <Typography>
                {index + 1}. {item.entityName}: {item.message}
              </Typography>
            ))}
          </Box>
        </GenericInfoModal>
      )}
    </>
  );
};

export default GenericImportButton;
